import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Car, ArrowLeft, FileText, DollarSign, Route, Users, History, 
  Plus, Upload, Trash2, Edit2, AlertTriangle, CheckCircle,
  Clock, Calendar, Fuel, CreditCard, Banknote, MapPin,
  ChevronDown, ChevronUp, RefreshCw, Eye, Download, Settings,
  CreditCard as PlateIcon, Palette, Zap, Gauge
} from 'lucide-react';
import api from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { toast } from 'sonner';
import { formatCurrency, formatEuro, lekToEuro } from '../../utils/formatters';

// Tab components
const TabButton = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition ${
      active 
        ? 'border-red-600 text-red-600 bg-red-50' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon size={18} />
    {label}
    {badge > 0 && (
      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        active ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
      }`}>
        {badge}
      </span>
    )}
  </button>
);

// Status badges
const getDocStatusBadge = (status, daysUntil) => {
  const styles = {
    'valid': 'bg-green-100 text-green-700',
    'expiring_soon': 'bg-yellow-100 text-yellow-700',
    'expired': 'bg-red-100 text-red-700'
  };
  const labels = {
    'valid': 'Valid',
    'expiring_soon': daysUntil !== undefined ? `${daysUntil} ditë` : 'Po skadon',
    'expired': 'Skaduar'
  };
  const icons = {
    'valid': <CheckCircle size={12} />,
    'expiring_soon': <Clock size={12} />,
    'expired': <AlertTriangle size={12} />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
      {icons[status]}
      {labels[status] || status}
    </span>
  );
};

const getDocTypeLabel = (type) => {
  const labels = {
    'insurance': 'Siguracion',
    'registration': 'Leje Qarkullimi',
    'inspection': 'Kolaudim',
    'permit': 'Liçencë Taxi',
    'tax': 'Taksa',
    'other': 'Tjetër'
  };
  return labels[type] || type;
};

const getCategoryLabel = (category) => {
  const labels = {
    'fuel': 'Karburant',
    'service': 'Servis',
    'insurance': 'Siguracioni',
    'tax': 'Taksa',
    'inspection': 'Kolaudim',
    'repair': 'Riparime',
    'other': 'Të tjera'
  };
  return labels[category] || category;
};

const getCategoryIcon = (category) => {
  const icons = {
    'fuel': <Fuel size={16} className="text-amber-500" />,
    'service': <Car size={16} className="text-blue-500" />,
    'insurance': <FileText size={16} className="text-purple-500" />,
    'tax': <DollarSign size={16} className="text-green-500" />,
    'inspection': <CheckCircle size={16} className="text-cyan-500" />,
    'repair': <AlertTriangle size={16} className="text-red-500" />,
    'other': <DollarSign size={16} className="text-gray-500" />
  };
  return icons[category] || icons['other'];
};

const VehicleProfilePage = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { formatDateTime } = useSettings();
  
  const [vehicle, setVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  
  // Edit vehicle state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expenseStats, setExpenseStats] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  
  // Rides state
  const [rides, setRides] = useState([]);
  const [ridesFilter, setRidesFilter] = useState({ date_from: '', date_to: '', payment_method: '' });
  const [ridesPage, setRidesPage] = useState(1);
  const [ridesPerPage, setRidesPerPage] = useState(10);
  
  // Assignments state
  const [assignments, setAssignments] = useState([]);
  
  // Audit log state
  const [auditLog, setAuditLog] = useState([]);

  // Document form
  const [docForm, setDocForm] = useState({
    document_type: 'insurance',
    document_name: '',
    provider: '',
    start_date: '',
    expiry_date: '',
    cost: 0,
    cost_lek: 0,
    notes: ''
  });

  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    category: 'fuel',
    amount: 0,
    amount_lek: 0,
    description: '',
    date: new Date().toISOString().split('T')[0],
    odometer: 0,
    payment_method: 'cash'
  });

  // Expense categories from settings
  const [expenseCategories, setExpenseCategories] = useState([]);

  // Fetch vehicle details
  const fetchVehicle = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/fleet/vehicles/${vehicleId}`);
      setVehicle(res.data);
      setEditForm(res.data);
    } catch (err) {
      console.error('Error fetching vehicle:', err);
      toast.error('Gabim në ngarkimin e automjetit');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  // Update vehicle
  const handleUpdateVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/fleet/vehicles/${vehicleId}`, editForm);
      toast.success('Automjeti u përditësua me sukses');
      setShowEditModal(false);
      fetchVehicle();
    } catch (err) {
      toast.error('Gabim në përditësimin e automjetit');
    }
  };

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get(`/fleet/vehicles/${vehicleId}/profile`);
      setProfile(res.data);
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  }, [vehicleId]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await api.get(`/fleet/vehicles/${vehicleId}/documents`);
      setDocuments(res.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, [vehicleId]);

  const fetchExpenses = useCallback(async () => {
    try {
      const [expensesRes, statsRes] = await Promise.all([
        api.get(`/fleet/vehicles/${vehicleId}/expenses`),
        api.get(`/fleet/vehicles/${vehicleId}/expenses/stats`)
      ]);
      setExpenses(expensesRes.data);
      setExpenseStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    }
  }, [vehicleId]);

  const fetchRides = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (ridesFilter.date_from) params.append('date_from', ridesFilter.date_from);
      if (ridesFilter.date_to) params.append('date_to', ridesFilter.date_to);
      if (ridesFilter.payment_method) params.append('payment_method', ridesFilter.payment_method);
      
      const res = await api.get(`/fleet/vehicles/${vehicleId}/rides?${params.toString()}`);
      setRides(res.data);
    } catch (err) {
      console.error('Error fetching rides:', err);
    }
  }, [vehicleId, ridesFilter]);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await api.get(`/fleet/vehicles/${vehicleId}/assignments`);
      setAssignments(res.data);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  }, [vehicleId]);

  const fetchAuditLog = useCallback(async () => {
    try {
      const res = await api.get(`/fleet/vehicles/${vehicleId}/audit-log`);
      setAuditLog(res.data);
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  }, [vehicleId]);

  useEffect(() => {
    fetchVehicle();
    fetchProfile();
    fetchExpenseCategories();
  }, [fetchVehicle, fetchProfile]);

  // Fetch expense categories from settings
  const fetchExpenseCategories = async () => {
    try {
      const res = await api.get('/settings/expense-categories');
      setExpenseCategories(res.data.categories || []);
    } catch (err) {
      console.error('Error fetching expense categories:', err);
      // Use default categories if fetch fails
      setExpenseCategories([
        { id: 'servis', name: 'Servis', active: true },
        { id: 'siguracion', name: 'Siguracion', active: true },
        { id: 'karburant', name: 'Karburant', active: true },
        { id: 'energji', name: 'Energji (Elektrike)', active: true },
        { id: 'kolaudim', name: 'Kolaudim', active: true },
        { id: 'taksa', name: 'Taksa', active: true },
        { id: 'other', name: 'Shpenzime të tjera', active: true }
      ]);
    }
  };

  useEffect(() => {
    if (activeTab === 'documents') fetchDocuments();
    if (activeTab === 'expenses') fetchExpenses();
    if (activeTab === 'rides') fetchRides();
    if (activeTab === 'assignments') fetchAssignments();
    if (activeTab === 'audit') fetchAuditLog();
  }, [activeTab, fetchDocuments, fetchExpenses, fetchRides, fetchAssignments, fetchAuditLog]);

  // Document handlers
  const handleDocSubmit = async (e) => {
    e.preventDefault();
    try {
      let fileUrl = docForm.file_url;
      
      // Upload file if selected
      if (docForm.file) {
        const formData = new FormData();
        formData.append('file', docForm.file);
        formData.append('vehicle_id', vehicleId);
        const uploadRes = await api.post('/fleet/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadRes.data.url;
      }
      
      const submitData = {
        document_type: docForm.document_type,
        document_name: docForm.document_name,
        provider: docForm.provider,
        start_date: docForm.start_date || null,
        expiry_date: docForm.expiry_date,
        cost_lek: docForm.cost_lek || 0,
        file_url: fileUrl
      };
      
      if (editingDoc) {
        await api.put(`/fleet/documents/${editingDoc.id}`, submitData);
        toast.success('Dokumenti u përditësua');
      } else {
        await api.post(`/fleet/vehicles/${vehicleId}/documents`, submitData);
        toast.success('Dokumenti u shtua');
      }
      setShowDocModal(false);
      setEditingDoc(null);
      setDocForm({
        document_type: 'insurance',
        document_name: '',
        provider: '',
        start_date: '',
        expiry_date: '',
        cost: 0,
        cost_lek: 0,
        notes: '',
        file: null
      });
      fetchDocuments();
    } catch (err) {
      console.error('Document save error:', err);
      toast.error('Gabim në ruajtjen e dokumentit');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Jeni i sigurt që doni të fshini këtë dokument?')) return;
    try {
      await api.delete(`/fleet/documents/${docId}`);
      toast.success('Dokumenti u fshi');
      fetchDocuments();
    } catch (err) {
      toast.error('Gabim në fshirjen e dokumentit');
    }
  };

  // Expense handlers
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/fleet/vehicles/${vehicleId}/expenses`, expenseForm);
      toast.success('Shpenzimi u regjistrua');
      setShowExpenseModal(false);
      setExpenseForm({
        category: 'fuel',
        amount: 0,
        amount_lek: 0,
        description: '',
        date: new Date().toISOString().split('T')[0],
        odometer: 0,
        payment_method: 'cash'
      });
      fetchExpenses();
    } catch (err) {
      toast.error('Gabim në regjistrimin e shpenzimit');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" />
          <p className="text-gray-500">Duke ngarkuar...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Car className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Automjeti nuk u gjet</p>
          <button 
            onClick={() => navigate('/admin/fleet')}
            className="mt-4 text-red-600 hover:underline"
          >
            Kthehu te Flota
          </button>
        </div>
      </div>
    );
  }

  // Calculate stats from profile data
  const stats = {
    rides: profile?.rides_count || vehicle?.total_rides || 0,
    revenue: profile?.vehicle?.total_revenue || vehicle?.total_revenue || 0,
    expenses: profile?.expenses?.total || 0,
    profit: profile?.profit || 0
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Back button */}
          <button 
            onClick={() => navigate('/admin/fleet')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition"
          >
            <ArrowLeft size={20} />
            <span>Kthehu te Flota</span>
          </button>
          
          {/* Vehicle info */}
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-white/10 rounded-xl flex items-center justify-center">
              <Car className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{vehicle.brand} {vehicle.model}</h1>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-mono font-bold">
                  {vehicle.taxi_id}
                </span>
              </div>
              <div className="flex items-center gap-4 text-white/90">
                <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-lg">
                  <CreditCard size={14} />
                  <span className="font-mono font-semibold">{vehicle.plate}</span>
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-lg">
                  <Calendar size={14} />
                  {vehicle.year}
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-lg">
                  <Palette size={14} />
                  {vehicle.color}
                </span>
                <span className={`flex items-center gap-1.5 px-3 py-1 rounded-lg ${
                  vehicle.fuel_type === 'elektrike' 
                    ? 'text-white' 
                    : 'bg-amber-500/30 text-amber-100'
                }`}
                style={vehicle.fuel_type === 'elektrike' ? { backgroundColor: 'rgb(0 247 90 / 59%)' } : {}}
                >
                  {vehicle.fuel_type === 'elektrike' ? <Zap size={14} /> : <Fuel size={14} />}
                  {vehicle.fuel_type === 'elektrike' ? 'Elektrike' : 'Karburant'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition"
              title="Ndrysho Automjetin"
            >
              <Edit2 size={18} />
              <span className="font-medium">Ndrysho</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 -mt-6 mb-[10px]">
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-purple-500">
            <div className="text-2xl font-bold text-gray-900">{stats.rides}</div>
            <div className="text-sm text-gray-500">Udhëtime</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-green-500">
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenue * 100)}</div>
            <div className="text-xs text-gray-400">{formatEuro(lekToEuro(stats.revenue * 100))}</div>
            <div className="text-sm text-gray-500">Të Ardhura Totale</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-red-500">
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats.expenses * 100)}</div>
            <div className="text-xs text-gray-400">{formatEuro(lekToEuro(stats.expenses * 100))}</div>
            <div className="text-sm text-gray-500">Shpenzime Totale</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-blue-500">
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats.profit * 100)}</div>
            <div className="text-xs text-gray-400">{formatEuro(lekToEuro(stats.profit * 100))}</div>
            <div className="text-sm text-gray-500">Fitimi</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-4">
        <div className="bg-white rounded-xl shadow-sm">
          <div className="flex border-b">
            <TabButton 
              active={activeTab === 'documents'} 
              onClick={() => setActiveTab('documents')}
              icon={FileText}
              label="Dokumentet"
              badge={documents.length}
            />
            <TabButton 
              active={activeTab === 'rides'} 
              onClick={() => setActiveTab('rides')}
              icon={Route}
              label="Rrugët"
              badge={rides.length}
            />
            <TabButton 
              active={activeTab === 'expenses'} 
              onClick={() => setActiveTab('expenses')}
              icon={DollarSign}
              label="Shpenzimet"
              badge={expenses.length}
            />
            <TabButton 
              active={activeTab === 'assignments'} 
              onClick={() => setActiveTab('assignments')}
              icon={Users}
              label="Shoferët"
              badge={assignments.length}
            />
            <TabButton 
              active={activeTab === 'audit'} 
              onClick={() => setActiveTab('audit')}
              icon={History}
              label="Historiku"
            />
          </div>

          <div className="p-6">
            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">Dokumentet Ligjore</h3>
                  <button
                    onClick={() => { setEditingDoc(null); setShowDocModal(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    <Plus size={18} />
                    Shto Dokument
                  </button>
                </div>

                {/* Document Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {documents.filter(d => d.status === 'valid').length}
                    </div>
                    <div className="text-sm text-green-700">Dokumenta Valid</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      {documents.filter(d => d.status === 'expiring_soon').length}
                    </div>
                    <div className="text-sm text-yellow-700">Po Skadojnë</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="text-2xl font-bold text-red-600">
                      {documents.filter(d => d.status === 'expired').length}
                    </div>
                    <div className="text-sm text-red-700">Skaduar</div>
                  </div>
                </div>

                {/* Documents Table */}
                {documents.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nuk ka dokumenta</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Dokumenti</th>
                          <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Furnitori</th>
                          <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Data Fillimit</th>
                          <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Data Mbarimit</th>
                          <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Çmimi</th>
                          <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Statusi</th>
                          <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Veprime</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {documents.map((doc) => (
                          <tr key={doc.id} className={`hover:bg-gray-50 ${
                            doc.status === 'expired' ? 'bg-red-50' : 
                            doc.status === 'expiring_soon' ? 'bg-yellow-50' : ''
                          }`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  doc.status === 'expired' ? 'bg-red-100' :
                                  doc.status === 'expiring_soon' ? 'bg-yellow-100' : 'bg-gray-100'
                                }`}>
                                  <FileText size={16} className={
                                    doc.status === 'expired' ? 'text-red-600' :
                                    doc.status === 'expiring_soon' ? 'text-yellow-600' : 'text-gray-500'
                                  } />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{doc.document_name || getDocTypeLabel(doc.document_type)}</div>
                                  <div className="text-xs text-gray-500">{getDocTypeLabel(doc.document_type)}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{doc.provider || '-'}</td>
                            <td className="px-4 py-3 text-center text-gray-600">
                              {doc.start_date ? (formatDateTime ? formatDateTime(doc.start_date, 'date') : new Date(doc.start_date).toLocaleDateString('sq-AL')) : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-medium ${
                                doc.status === 'expired' ? 'text-red-600' :
                                doc.status === 'expiring_soon' ? 'text-yellow-600' : 'text-gray-700'
                              }`}>
                                {formatDateTime ? formatDateTime(doc.expiry_date, 'date') : new Date(doc.expiry_date).toLocaleDateString('sq-AL')}
                              </span>
                              {doc.status === 'expiring_soon' && doc.days_until_expiry !== undefined && (
                                <div className="text-xs text-yellow-600 font-medium">({doc.days_until_expiry} ditë)</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {doc.cost_lek ? formatCurrency(doc.cost_lek) : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {getDocStatusBadge(doc.status, doc.days_until_expiry)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => { setEditingDoc(doc); setDocForm(doc); setShowDocModal(true); }}
                                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                                  title="Ndrysho"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteDoc(doc.id)}
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition"
                                  title="Fshi"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Expenses Tab */}
            {activeTab === 'expenses' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">Shpenzimet</h3>
                  <button
                    onClick={() => setShowExpenseModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    <Plus size={18} />
                    Regjistro Shpenzim
                  </button>
                </div>

                {/* Expense Stats */}
                {expenseStats && (
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="text-xl font-bold text-amber-600">
                        {formatCurrency(expenseStats.by_category?.fuel || 0)}
                      </div>
                      <div className="text-sm text-amber-700">Karburant</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="text-xl font-bold text-blue-600">
                        {formatCurrency(expenseStats.by_category?.service || 0)}
                      </div>
                      <div className="text-sm text-blue-700">Servis</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                      <div className="text-xl font-bold text-purple-600">
                        {formatCurrency(expenseStats.by_category?.insurance || 0)}
                      </div>
                      <div className="text-sm text-purple-700">Siguracioni</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <div className="text-xl font-bold text-gray-600">
                        {(expenseStats.total || 0).toLocaleString()} L
                      </div>
                      <div className="text-sm text-gray-700">Totali</div>
                    </div>
                  </div>
                )}

                {/* Expenses List */}
                {expenses.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nuk ka shpenzime të regjistruara</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expenses.map((expense) => (
                      <div key={expense.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            {getCategoryIcon(expense.category)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{getCategoryLabel(expense.category)}</div>
                            <div className="text-sm text-gray-500">
                              {expense.description && `${expense.description} • `}
                              {formatDateTime ? formatDateTime(expense.date, 'date') : expense.date}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">{expense.amount_lek?.toLocaleString()} L</div>
                          <div className="text-sm text-gray-500">{expense.amount?.toFixed(2)} €</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Rides Tab */}
            {activeTab === 'rides' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">Rrugët e Kryera</h3>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">Shfaq:</label>
                    <select
                      value={ridesPerPage}
                      onChange={(e) => { setRidesPerPage(parseInt(e.target.value)); setRidesPage(1); }}
                      className="px-3 py-1.5 border rounded-lg text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
                {rides.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Route className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nuk ka rrugë të regjistruara</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white rounded-xl border overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Destinacion</th>
                            <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Nisje</th>
                            <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Mberritje</th>
                            <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Data</th>
                            <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Ora</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Shoferi</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Pagesa</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {rides
                            .slice((ridesPage - 1) * ridesPerPage, ridesPage * ridesPerPage)
                            .map((ride) => {
                              const rideDate = ride.created_at ? new Date(ride.created_at) : null;
                              return (
                                <tr key={ride.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    <div className="flex flex-col">
                                      <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={ride.dropoff_address}>
                                        {ride.dropoff_address || '-'}
                                      </span>
                                      <span className="text-xs text-gray-500 truncate max-w-[200px]" title={ride.pickup_address}>
                                        Nga: {ride.pickup_address || '-'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                                    {ride.started_at ? new Date(ride.started_at).toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                                    {ride.completed_at ? new Date(ride.completed_at).toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                                    {rideDate ? rideDate.toLocaleDateString('sq-AL') : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                                    {rideDate ? rideDate.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {ride.driver_name || '-'}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className="font-semibold text-gray-900">{((ride.final_price || 0) * 100).toLocaleString()} L</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        ride.payment_method === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {ride.payment_method === 'cash' ? 'Cash' : 'Kartë'}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {rides.length > ridesPerPage && (
                      <div className="flex items-center justify-between mt-4 px-2">
                        <p className="text-sm text-gray-500">
                          Shfaqur {(ridesPage - 1) * ridesPerPage + 1} - {Math.min(ridesPage * ridesPerPage, rides.length)} nga {rides.length} rrugë
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setRidesPage(p => Math.max(1, p - 1))}
                            disabled={ridesPage === 1}
                            className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Para
                          </button>
                          {Array.from({ length: Math.ceil(rides.length / ridesPerPage) }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === Math.ceil(rides.length / ridesPerPage) || Math.abs(p - ridesPage) <= 1)
                            .map((p, idx, arr) => (
                              <React.Fragment key={p}>
                                {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400">...</span>}
                                <button
                                  onClick={() => setRidesPage(p)}
                                  className={`w-8 h-8 rounded-lg text-sm ${
                                    ridesPage === p 
                                      ? 'bg-red-600 text-white' 
                                      : 'hover:bg-gray-100'
                                  }`}
                                >
                                  {p}
                                </button>
                              </React.Fragment>
                            ))}
                          <button
                            onClick={() => setRidesPage(p => Math.min(Math.ceil(rides.length / ridesPerPage), p + 1))}
                            disabled={ridesPage >= Math.ceil(rides.length / ridesPerPage)}
                            className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Pas
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Assignments Tab */}
            {activeTab === 'assignments' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Shoferët e Caktuar</h3>
                {assignments.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nuk ka shoferë të caktuar</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <span className="text-red-600 font-bold">
                              {assignment.driver_name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{assignment.driver_name}</div>
                            <div className="text-sm text-gray-500">
                              {formatDateTime ? formatDateTime(assignment.assigned_at) : assignment.assigned_at}
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          assignment.is_current ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {assignment.is_current ? 'Aktual' : 'I mëparshëm'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && (
              <div>
                <h3 className="text-lg font-semibold mb-6">Historiku i Ndryshimeve</h3>
                {auditLog.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nuk ka historik</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditLog.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm shrink-0">
                          <History size={20} className="text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{log.action}</div>
                          <div className="text-sm text-gray-500">
                            {log.user_name} • {formatDateTime ? formatDateTime(log.timestamp) : log.timestamp}
                          </div>
                          {log.details && (
                            <div className="mt-2 text-sm text-gray-600 bg-white p-2 rounded">
                              {JSON.stringify(log.details)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Modal */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingDoc ? 'Ndrysho Dokument' : 'Shto Dokument të Ri'}
              </h3>
              <button onClick={() => { setShowDocModal(false); setEditingDoc(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} />
              </button>
            </div>
            <form onSubmit={handleDocSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lloji i Dokumentit</label>
                  <select
                    value={docForm.document_type}
                    onChange={(e) => setDocForm({...docForm, document_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="insurance">Siguracion</option>
                    <option value="registration">Leje Qarkullimi</option>
                    <option value="inspection">Kolaudim</option>
                    <option value="permit">Liçencë Taxi</option>
                    <option value="other">Tjetër</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Furnitori</label>
                  <input
                    type="text"
                    value={docForm.provider || ''}
                    onChange={(e) => setDocForm({...docForm, provider: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="p.sh. SIGAL, EUROSIG"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emri i Dokumentit</label>
                <input
                  type="text"
                  value={docForm.document_name || ''}
                  onChange={(e) => setDocForm({...docForm, document_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="p.sh. Siguracion SIGAL 2026"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Fillimit</label>
                  <input
                    type="date"
                    value={docForm.start_date ? docForm.start_date.split('T')[0] : ''}
                    onChange={(e) => setDocForm({...docForm, start_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Skadimit</label>
                  <input
                    type="date"
                    value={docForm.expiry_date ? docForm.expiry_date.split('T')[0] : ''}
                    onChange={(e) => setDocForm({...docForm, expiry_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Çmimi (LEK)</label>
                <input
                  type="number"
                  value={docForm.cost_lek || ''}
                  onChange={(e) => setDocForm({...docForm, cost_lek: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skedar (Foto/PDF)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition cursor-pointer">
                  <input
                    type="file"
                    id="docFile"
                    accept="image/*,.pdf"
                    onChange={(e) => setDocForm({...docForm, file: e.target.files[0]})}
                    className="hidden"
                  />
                  <label htmlFor="docFile" className="cursor-pointer">
                    {docForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <CheckCircle size={20} />
                        <span className="font-medium">{docForm.file.name}</span>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">Kliko për të ngarkuar foto ose PDF</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, PDF deri në 5MB</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowDocModal(false); setEditingDoc(null); }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {editingDoc ? 'Ruaj Ndryshimet' : 'Shto Dokument'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Regjistro Shpenzim</h3>
              <button onClick={() => setShowExpenseModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} />
              </button>
            </div>
            <form onSubmit={handleExpenseSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria</label>
                <select
                  value={expenseForm.category}
                  onChange={(e) => setExpenseForm({...expenseForm, category: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {expenseCategories.filter(c => c.active !== false).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shuma (LEK)</label>
                  <input
                    type="number"
                    value={expenseForm.amount_lek}
                    onChange={(e) => setExpenseForm({...expenseForm, amount_lek: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Përshkrimi</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="p.sh. Ndërrimi i vajit"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowExpenseModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Regjistro
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Vehicle Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">Ndrysho Automjetin</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateVehicle} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marka</label>
                  <input
                    type="text"
                    value={editForm.brand || ''}
                    onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modeli</label>
                  <input
                    type="text"
                    value={editForm.model || ''}
                    onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Targa</label>
                  <input
                    type="text"
                    value={editForm.plate || ''}
                    onChange={(e) => setEditForm({...editForm, plate: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Viti</label>
                  <input
                    type="number"
                    value={editForm.year || ''}
                    onChange={(e) => setEditForm({...editForm, year: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngjyra</label>
                  <input
                    type="text"
                    value={editForm.color || ''}
                    onChange={(e) => setEditForm({...editForm, color: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria</label>
                  <select
                    value={editForm.vehicle_category || 'sedan'}
                    onChange={(e) => setEditForm({...editForm, vehicle_category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="van">Van</option>
                    <option value="minivan">Minivan</option>
                    <option value="luxury">Luksoz</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Karburanti</label>
                  <select
                    value={editForm.fuel_type || 'karburant'}
                    onChange={(e) => setEditForm({...editForm, fuel_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="karburant">Karburant</option>
                    <option value="elektrike">Elektrike</option>
                    <option value="hibrid">Hibrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vende</label>
                  <input
                    type="number"
                    value={editForm.seats || 4}
                    onChange={(e) => setEditForm({...editForm, seats: parseInt(e.target.value) || 4})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statusi</label>
                  <select
                    value={editForm.status || 'active'}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="active">Aktiv</option>
                    <option value="in_service">Në Servis</option>
                    <option value="offline">Offline</option>
                    <option value="disabled">I Çaktivizuar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transmetimi</label>
                  <select
                    value={editForm.transmission || 'automatic'}
                    onChange={(e) => setEditForm({...editForm, transmission: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="automatic">Automatik</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
              </div>
              
              {/* Expense Quick Add */}
              <div className="border-t pt-4 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setShowExpenseModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition"
                >
                  <DollarSign size={18} />
                  Regjistro Shpenzim
                </button>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Ruaj Ndryshimet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleProfilePage;
